'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { getVerses } from '@/lib/mockBibleData';
import PropTypes from 'prop-types';

export default function CrossReferencePopup({ reference, position, onClose }) {
  const router = useRouter();
  const [verseText, setVerseText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Parse reference (e.g., "John 3:16")
    const parseReference = () => {
      const parts = reference?.reference?.split(' ');
      const book = parts?.[0]?.toLowerCase();
      const [chapter, verse] = parts?.[1]?.split(':')?.map(Number);
      
      const verses = getVerses(book, chapter, 'kjv');
      const foundVerse = verses?.find(v => v?.verse === verse);
      
      setVerseText(foundVerse?.text || 'Verse not found in mock data');
      setLoading(false);
    };

    parseReference();
  }, [reference]);

  const handleGoToVerse = () => {
    const parts = reference?.reference?.split(' ');
    const book = parts?.[0]?.toLowerCase();
    const [chapter, verse] = parts?.[1]?.split(':');
    router?.push(`/verse/${book}/${chapter}/${verse}`);
    onClose();
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e?.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50"
        onClick={onClose}
      />
      {/* Popup */}
      <div
        className="fixed z-50 w-80 bg-card border-2 border-primary rounded-lg shadow-2xl p-4"
        style={{
          top: position?.top + position?.height + 10,
          left: Math.max(10, position?.left - 160),
        }}
      >
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-sm font-bold text-primary">
            {reference?.reference}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-accent transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <p className="text-sm leading-relaxed mb-3">
              {verseText}
            </p>

            <button
              onClick={handleGoToVerse}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              Go to Verse
            </button>
          </>
        )}
      </div>
    </>
  );
}

CrossReferencePopup.propTypes = {
  reference: PropTypes?.shape({
    reference: PropTypes?.string?.isRequired,
    text: PropTypes?.string
  })?.isRequired,
  position: PropTypes?.shape({
    top: PropTypes?.number?.isRequired,
    left: PropTypes?.number?.isRequired,
    height: PropTypes?.number?.isRequired
  })?.isRequired,
  onClose: PropTypes?.func?.isRequired
};